import { Avatar, Box, Button, IconButton, keyframes, Menu, MenuItem, Paper, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { MouseEvent, useState } from 'react';

import { getFormattedDateString, Markdown, useUserDetailsStore } from '@zeta-core';
import { MoreVerticalFillIcon, PencilEditIcon2, SuccessCheckIcon, ZetaAiIcon } from '@zeta-core/assets';
import { InteractionSubSectionType } from '@zeta-services';

type MessageCardProps = {
  item: any; //change
  editMode: {
    id: number;
    content: string;
  };
  setEditMode: (value: { id: number; content: string }) => void;
  index: number;
  handleResend: (resend: boolean, content: string, index: number) => void;
  handleMark?: (index: number) => void;
  markedIndex: number;
  hideSave?: boolean;
  readOnly?: boolean;
};

const MessageCard = ({
  item,
  index,
  editMode = {
    id: -1,
    content: '',
  },
  setEditMode,
  handleResend,
  handleMark,
  markedIndex,
  hideSave = false,
  readOnly = false,
}: MessageCardProps) => {
  const theme = useTheme();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const userStore = useUserDetailsStore((state) => state.user);
  const { firstName, lastName } = userStore;

  const modifiedText =
    typeof item.content === 'string' ? item?.content?.replace(/\\u([0-9a-fA-F]{4})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16))) : '';
  // replace text in thsi enum InteractionSubSectionType in below text with ''
  const removeSectionNames = (text: string) => {
    const sectionNames = Object.values(InteractionSubSectionType);
    return sectionNames.reduce((acc, section) => acc.replace(`${section}:`, ''), text);
  };

  const { text: displayText, thinking } = processThinkTags(modifiedText);

  const handleClickMore = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
  };
  const handleClose = (event: unknown) => {
    (event as MouseEvent<HTMLElement>).stopPropagation();
    (event as MouseEvent<HTMLElement>).preventDefault();
    setAnchorElement(null);
  };

  return (
    <Paper
      sx={{
        borderLeft: `2px solid ${item?.role === 'user' ? theme.palette.grey[800] : theme.palette.primary.main}`,
        borderRadius: 0,
        mb: 2.5,
        boxShadow: 'none',
        '&:hover': {
          '.edit-icon': {
            visibility: 'visible',
          },
        },
      }}
    >
      <Stack gap={3} sx={{ p: 1.5 }}>
        <Stack justifyContent="space-between">
          <Stack flexDirection="row" gap={2} justifyContent="space-between">
            <Stack flexDirection="row" gap={2} alignItems="center">
              {item?.role === 'user' ? <Avatar>{firstName.charAt(0)}</Avatar> : <ZetaAiIcon />}
              <Stack>
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }} id={`title-${index}`}>
                  {item?.role === 'user' ? `${firstName} ${lastName}` : 'Zeta AI'}
                </Typography>
                <Typography variant="body2" color="text.secondary" id="time">
                  {getFormattedDateString(item?.timestamp, 'DD MMM YYYY HH:mm', '', 'Europe/London')}
                </Typography>
              </Stack>
            </Stack>

            {item?.role === 'user' && markedIndex < index && !readOnly && (
              <Stack alignSelf={'end'} sx={{ cursor: 'pointer', visibility: 'hidden' }} className="edit-icon">
                <IconButton
                  id={`edit-${index}`}
                  onClick={() => {
                    setEditMode({ id: index, content: item.content });
                  }}
                  size="large"
                >
                  <PencilEditIcon2 />
                </IconButton>
              </Stack>
            )}
            {item?.role !== 'user' && !hideSave && markedIndex < index && (
              <IconButton
                onClick={handleClickMore}
                className="edit-icon"
                aria-controls={anchorElement ? 'menu' : undefined}
                aria-haspopup="true"
                aria-expanded={anchorElement ? 'true' : undefined}
                sx={{
                  width: 40,
                  height: 40,
                  visibility: 'hidden',
                }}
              >
                <MoreVerticalFillIcon />
              </IconButton>
            )}

            {markedIndex === index && markedIndex > 0 && (
              <Tooltip title="Marked" placement="bottom" arrow>
                <IconButton sx={{ alignSelf: 'center' }} id={`marked-${index}`}>
                  <SuccessCheckIcon />
                </IconButton>
              </Tooltip>
            )}

            <Menu
              open={!!anchorElement}
              onClose={handleClose}
              anchorEl={anchorElement}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={(event) => {
                  if (handleMark) handleMark(index);
                  handleClose(event);
                }}
                sx={{ my: 1, mx: 2, fontWeight: 'fontWeightBold', color: 'text.primary', justifyContent: 'center' }}
              >
                Save
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        {editMode?.id === index ? (
          <Stack sx={{ flexDirection: 'column' }} gap={2}>
            <TextField multiline fullWidth value={editMode?.content} onChange={(e) => setEditMode({ ...editMode, content: e.target.value })} />
            <Stack sx={{ flexDirection: 'row', justifyContent: 'flex-end' }} gap={2}>
              <Button
                onClick={() =>
                  setEditMode({
                    id: -1,
                    content: '',
                  })
                }
                variant="soft"
              >
                Cancel
              </Button>
              <Button color="primary" variant="contained" onClick={() => handleResend(true, editMode?.content, index)}>
                Send
              </Button>
            </Stack>
          </Stack>
        ) : (
          <>
            {thinking && <PulsatingCircle />}
            <Markdown children={removeSectionNames(displayText) ?? ''} />
          </>
        )}
        {item?.showImage && (
          <img
            src="/aibot.jpg"
            alt="attachment"
            style={{
              width: '100px',
              height: 'auto',
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[1],
            }}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default MessageCard;

export function processThinkTags(text: string) {
  const thinkOpen = text.indexOf('<think>');
  const thinkClose = text.indexOf('</think>');

  if (thinkOpen !== -1 && thinkClose !== -1 && thinkClose > thinkOpen) {
    return { text: text.replace(/<think>[\s\S]*?<\/think>/g, ''), thinking: false };
  }

  if (thinkOpen !== -1 && thinkClose === -1) {
    return { text: text.slice(0, thinkOpen), thinking: true };
  }

  return { text, thinking: false };
}

const pulseDot = keyframes`
  0% {
    transform: scale(0.8);
  }
  50% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.8);
  }
`;

export function PulsatingCircle() {
  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        ml: 2,
        mt: 2,
        transform: 'translate(-50%, -50%)',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          display: 'block',
          width: '100%',
          height: '100%',
          backgroundColor: 'black',
          borderRadius: '15px',
          boxShadow: '0 0 8px rgba(0, 0, 0, 0.3)',
          animation: `${pulseDot} 1.25s cubic-bezier(0.455, 0.03, 0.515, 0.955) -0.4s infinite`,
        },
      }}
    />
  );
}
